import { useRef, useState } from 'react'
import { Link, LinkProps, useMatch, useResolvedPath } from 'react-router-dom'

type IconProps = {
    Icon: any
    label: string
    linkTo: string
    end?: boolean
    disabled?: boolean
    collapsed?: boolean
}

export const SidebarIcon = ({
    Icon,
    label,
    linkTo,
    end = false,
    disabled,
    collapsed = false,
}: IconProps) => {
    const itemRef = useRef<HTMLDivElement>(null)
    const [tooltipTop, setTooltipTop] = useState(0)

    const handleMouseEnter = () => {
        if (!itemRef.current) return
        const rect = itemRef.current.getBoundingClientRect()
        setTooltipTop(rect.top + rect.height / 2)
    }

    return (
        <CustomLink disabled={disabled} to={linkTo} end={end} variant={collapsed ? 'rootCollapsed' : 'root'}>
            <div
                ref={itemRef}
                onMouseEnter={handleMouseEnter}
                className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start gap-2.5'}`}
            >
                <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
                        collapsed
                            ? ''
                            : 'bg-slate-100 text-current ring-1 ring-slate-200/70 group-hover:bg-blue-50 dark:bg-slate-800 dark:ring-slate-700 dark:group-hover:bg-blue-900/40'
                    }`}
                >
                    <Icon className="h-4 w-4" />

                    {collapsed && (
                        <span
                            className="fixed left-[70px] z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-950 px-2.5 py-1.5 text-xs text-white shadow-lg group-hover:block"
                            style={{ top: tooltipTop }}
                        >
                            {label}
                        </span>
                    )}
                </span>

                {!collapsed && (
                    <span className="block min-w-0 truncate text-sm font-medium capitalize">
                        {label}
                    </span>
                )}
            </div>
        </CustomLink>
    )
}

interface CustomLinkProp extends LinkProps {
    end?: boolean
    disabled?: boolean
    variant?: 'root' | 'rootCollapsed' | 'child' | 'floating'
}

export const CustomLink = ({
    children,
    to,
    end = false,
    disabled,
    variant = 'root',
    ...props
}: CustomLinkProp) => {
    const resolved = useResolvedPath(to)
    const match = useMatch({ path: resolved.pathname, end })

    const styles = {
        root: {
            base: 'group mx-1.5 my-0.5 block rounded-xl px-2.5 py-2.5 text-slate-700 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300',
            active: 'group mx-1.5 my-0.5 block rounded-xl bg-white px-2.5 py-2.5 text-blue-700 shadow-sm ring-1 ring-blue-100 transition-all duration-200 hover:bg-white hover:text-blue-700 dark:bg-slate-900 dark:text-blue-300 dark:ring-blue-900/70',
            disabled: 'mx-1.5 my-0.5 block rounded-xl px-2.5 py-2.5 text-gray-400 dark:text-gray-600 pointer-events-none select-none',
        },
        rootCollapsed: {
            base: 'group mx-1 my-0.5 block rounded-xl px-2 py-2.5 text-slate-600 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300',
            active: 'group mx-1 my-0.5 block rounded-xl bg-blue-600 px-2 py-2.5 text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-600 hover:text-white dark:bg-blue-500 dark:text-white',
            disabled: 'mx-1 my-0.5 block rounded-xl px-2 py-2.5 text-gray-400 dark:text-gray-600 pointer-events-none select-none',
        },
        child: {
            base: 'group my-0.5 flex items-center rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-all duration-200 hover:bg-white hover:text-blue-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-blue-300',
            active: 'group my-0.5 flex items-center rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 transition-all duration-200 dark:bg-blue-950/60 dark:text-blue-300 dark:ring-blue-900',
            disabled: 'my-0.5 flex items-center rounded-lg px-2.5 py-1.5 text-xs text-gray-400 dark:text-gray-600 pointer-events-none select-none',
        },
        floating: {
            base: 'group flex items-center rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition-all duration-200 hover:bg-slate-100 hover:text-blue-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-blue-300',
            active: 'group flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-200 dark:bg-blue-500',
            disabled: 'flex items-center rounded-lg px-3 py-1.5 text-xs text-gray-400 dark:text-gray-600 pointer-events-none select-none',
        },
    }[variant]

    return (
        <Link
            className={disabled ? styles.disabled : match ? styles.active : styles.base}
            to={to}
            {...props}
        >
            {children}
        </Link>
    )
}
